import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import { EditStatusType, StateHelperService } from '@Core/services/proprietary/state-helper.service';
import { cloneDeep } from 'src/ep-utils';
import {
    AddAddress,
    ClearThirdPartyAddress,
    CloneAddress,
    DeleteAddress,
    RestoreAddress,
    SetThirdPartyAddresses,
    UpdateAddress
} from './address.actions';
import { ContractObjectDefaults } from './contract-object.defaults';
import { AddEntityAddressLink, RemoveEntityAddressLink, UpdateEntity } from './entity.actions';
import { EntityState } from './entity.state';

export interface AddressStateModel {
    address: any;
    currentID: string;
}

@State<AddressStateModel>({
    name: 'addressState',
    defaults: {
        address: {},
        currentID: null
    }
})
@Injectable()
export class AddressState {
    constructor(private store: Store, private helper: StateHelperService) { }

    //******* SELECTORS *********

    @Selector()
    static fullState(state: AddressStateModel) {
        //temporary selector for debugging
        return state.address;
    }

    @Selector()
    static addressByID(state: AddressStateModel) {
        return (addressID: string) => {
            if (state.address[addressID]) {
                return state.address[addressID];
            } else {
                return {};
            }
        };
    }

    @Selector()
    static currentAddress(state: AddressStateModel) {
        if (state.address[state.currentID]) {
            return state.address[state.currentID];
        } else {
            return {};
        }
    }

    @Selector()
    static addresses(state: AddressStateModel) {
        //return all addresses
        let result: any = [];
        for (var i in state.address) {
            result.push(state.address[i]);
        }
        return result;
    }

    @Selector()
    static addressesByIDs(state: AddressStateModel) {
        //return addresses based on an array of address_ID passed in
        return (addressIDs: any[]) => {
            let result: any = [];
            if (addressIDs) {
                addressIDs.forEach(function (thisID) {
                    result.push(state.address[thisID]);
                });
            }
            return result;
        };
    }

    //******  ACTIONS  ******
    @Action(UpdateAddress)
    updateAddress(sc: StateContext<AddressStateModel>, action: UpdateAddress) {
        const state = sc.getState();
        let addressID = action.address.Address_ID;
        let updateAddress: any = { ...action.address }; //need DEEP copy?  -- subloc list????
        const currentAddress = state.address[addressID];
        this.helper.setItemChanged(updateAddress, state.address[addressID].Edit_Status);

        sc.patchState({
            address: {
                ...state.address,
                [addressID]: {
                    ...state.address[addressID],
                    ...updateAddress
                }
            }
        });

        // If Address record's Dflt_Address_Ind is changed to "Y", Set to "N" for all other Address Records for the same entity.
        if (updateAddress.Dflt_Address_Ind != currentAddress.Dflt_Address_Ind && updateAddress.Dflt_Address_Ind == 'Y') {
            let entity = this.store.selectSnapshot(EntityState.entityByID)(action.entityID);
            let addressIDs = [];
            if (entity) {
                if (entity.Addresses && entity.Addresses.Address) {
                    for (let i in entity.Addresses.Address) {
                        addressIDs.push(entity.Addresses.Address[i]);
                    }
                }
            }

            if (addressIDs.length > 0) {
                let updateStateAddress = cloneDeep(state.address);

                for (var i in updateStateAddress) {
                    let id = updateStateAddress[i].Address_ID;
                    if (addressIDs.indexOf(id) === -1) {
                        continue;
                    } else {
                        this.helper.setItemChanged(updateStateAddress[i], updateStateAddress[i].Edit_Status);
                        if (updateStateAddress[i].Address_ID == addressID) {
                            updateStateAddress[i].Dflt_Address_Ind = 'Y';
                        } else {
                            updateStateAddress[i].Dflt_Address_Ind = 'N';
                        }
                    }
                }

                sc.patchState({ address: updateStateAddress });
            }
        }

        if (action.entityID) {
            this.store.dispatch(new UpdateEntity({ Entity_ID: action.entityID }));
        }
    }

    @Action(DeleteAddress)
    deleteAddress(sc: StateContext<AddressStateModel>, action: DeleteAddress) {
        const state = sc.getState();
        let address = { ...state.address }; //shallow copy address from state
        this.helper.setItemDeleted(address, action.addressID);
        sc.patchState({ address: address });
        this.store.dispatch(new RemoveEntityAddressLink(action.entityID, action.addressID));
    }

    @Action(RestoreAddress)
    restoreAddress(sc: StateContext<AddressStateModel>, action: RestoreAddress) {
        const state = sc.getState();
        let address = { ...state.address }; //shallow copy address from state
        this.helper.setItemRestored(address, action.addressID);
        sc.patchState({ address: address });
    }

    @Action(AddAddress)
    addAddress(sc: StateContext<AddressStateModel>, action: AddAddress) {
        const state = sc.getState();
        let newAddressID: any = this.helper.getTempID();
        let newAddress = this.helper.createDefaultItem(ContractObjectDefaults, 'Address', action.address);
        this.helper.setItemAdded(newAddress);
        newAddress.Address_ID = newAddressID;

        //set default ind and address type on first address for the entity
        let entity = this.store.selectSnapshot(EntityState.entityByID)(action.entityID);
        let addressExists: boolean = false;
        if (entity) {
            if (entity.Address || (entity.Addresses && entity.Addresses.Address && entity.Addresses.Address.length > 0)) {
                addressExists = true;
            }
        }

        if (addressExists == false) {
            newAddress.Dflt_Address_Ind = 'Y';
            newAddress.Address_Type = 'BS';
        }

        sc.patchState({
            currentID: newAddressID,
            address: { ...state.address, [newAddressID]: newAddress }
        });

        this.store.dispatch(new AddEntityAddressLink(action.entityID, newAddressID));
    }

    @Action(CloneAddress)
    cloneAddress(sc: StateContext<AddressStateModel>, action: CloneAddress) {
        const state = sc.getState();
        let srcID = action.addressID;
        let newAddressID: any = this.helper.getTempID();
        let newAddress = cloneDeep(state.address[srcID]);
        newAddress.Address_ID = newAddressID;
        this.helper.setItemAdded(newAddress);
        if (state.address[srcID].Edit_Status != EditStatusType.Delete) {
            newAddress.Address_Type = null; // if src address not deleted then clear address type on new address
            if (state.address[srcID].Dflt_Address_Ind == 'Y') {
                newAddress.Dflt_Address_Ind = 'N';
            }
        }

        sc.patchState({
            currentID: newAddressID,
            address: { ...state.address, [newAddressID]: newAddress }
        });

        this.store.dispatch(new AddEntityAddressLink(action.entityID, newAddressID));
    }

    @Action(SetThirdPartyAddresses)
    setThirdPartyAddresses(sc: StateContext<AddressStateModel>, action: SetThirdPartyAddresses) {
        const state = sc.getState();
        const addresses = cloneDeep(state.address);
        let newAddresses = cloneDeep(action.addresses);

        for (var i in newAddresses) {
            newAddresses[i].Edit_Status = EditStatusType.NoChange;
            addresses[newAddresses[i].Address_ID] = newAddresses[i];
        }
        sc.patchState({
            address: addresses
        });
    }

    @Action(ClearThirdPartyAddress)
    clearThirdPartyAddress(sc: StateContext<AddressStateModel>, action: ClearThirdPartyAddress) {
        const state = sc.getState();
        let address = { ...state.address }; //shallow copy address from state
        delete address[action.addressID];
        sc.patchState({ address: address });
    }
}
